import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Agent } from "@ligament/shared";
import { agentsApi } from "../../api/agents";
import { metricAlertsApi, type ThresholdBaselineResult } from "../../api/metricAlerts";
import { queryKeys } from "../../lib/queryKeys";
import { cn } from "../../lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Ban, Loader2, Sparkles } from "lucide-react";

interface KpiThreshold {
  key: string;
  label: string;
  lookerField: string;
  condition: string;
  warningValue: number;
  criticalValue: number;
  unsupported?: boolean;
  explore?: string;
  filters?: Record<string, string>;
  // Preserve any other runtimeConfig fields (model, query, measureField) on save.
  [extra: string]: unknown;
}

function computeStatus(
  value: number | null | undefined,
  threshold: KpiThreshold,
): "ok" | "warning" | "critical" | null {
  if (value === null || value === undefined) return null;
  const { condition, warningValue, criticalValue } = threshold;
  switch (condition) {
    case ">":
      if (value > criticalValue) return "critical";
      if (value > warningValue) return "warning";
      return "ok";
    case ">=":
      if (value >= criticalValue) return "critical";
      if (value >= warningValue) return "warning";
      return "ok";
    case "<":
      if (value < criticalValue) return "critical";
      if (value < warningValue) return "warning";
      return "ok";
    case "<=":
      if (value <= criticalValue) return "critical";
      if (value <= warningValue) return "warning";
      return "ok";
    case "=":
      if (value === criticalValue) return "critical";
      if (value === warningValue) return "warning";
      return "ok";
    default:
      return "ok";
  }
}

function StatusPill({ status }: { status: "ok" | "warning" | "critical" | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
        status === "ok" && "bg-green-500/10 text-green-600 dark:text-green-400",
        status === "warning" && "bg-amber-500/10 text-amber-600 dark:text-amber-400",
        status === "critical" && "bg-red-500/10 text-red-600 dark:text-red-400",
      )}
    >
      {status}
    </span>
  );
}

export function ThresholdsTab({
  agent,
  companyId,
}: {
  agent: Agent;
  companyId: string;
}) {
  if (agent.agentType !== "metric_monitor") return null;

  const queryClient = useQueryClient();

  const rawThresholds =
    ((agent.runtimeConfig as Record<string, unknown>)?.thresholds as KpiThreshold[] | undefined) ?? [];

  const [thresholds, setThresholds] = useState<KpiThreshold[]>(
    // Spread the raw object so passthrough fields (unsupported, model, explore,
    // filters, query, measureField) survive a Save — only normalize the edited ones.
    rawThresholds.map((t) => ({
      ...t,
      condition: t.condition ?? ">",
      warningValue: Number(t.warningValue ?? 0),
      criticalValue: Number(t.criticalValue ?? 0),
    })),
  );

  const [saveMessage, setSaveMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  // Data-driven suggestion state, keyed by threshold.key.
  const [suggestions, setSuggestions] = useState<Record<string, ThresholdBaselineResult>>({});
  const [suggestingKey, setSuggestingKey] = useState<string | null>(null);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  async function suggestFromData(index: number) {
    const t = thresholds[index];
    if (!t) return;
    setSuggestError(null);
    setSuggestingKey(t.key);
    try {
      const result = await metricAlertsApi.thresholdBaseline(companyId, {
        measureField: t.lookerField,
        condition: t.condition,
      });
      setSuggestions((prev) => ({ ...prev, [t.key]: result }));
      setThresholds((prev) =>
        prev.map((row, i) =>
          i === index
            ? {
                ...row,
                warningValue: result.suggestion.warningValue,
                criticalValue: result.suggestion.criticalValue,
              }
            : row,
        ),
      );
    } catch (err) {
      setSuggestError(
        `Suggestion failed for ${t.label}: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setSuggestingKey(null);
    }
  }

  // Live KPI values from state.json (written by MetricMonitorRunner)
  const currentKpis = (
    (agent.runtimeConfig as Record<string, unknown>)?.state as Record<string, unknown> | undefined
  )?.currentKpis as Record<string, number | null> | undefined;

  const updateAgent = useMutation({
    mutationFn: (updatedThresholds: KpiThreshold[]) =>
      agentsApi.update(
        agent.id,
        {
          runtimeConfig: {
            ...(agent.runtimeConfig as Record<string, unknown>),
            thresholds: updatedThresholds,
          },
        },
        companyId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agent.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(agent.urlKey) });
      setSaveMessage({ type: "success", text: "Thresholds saved." });
      setTimeout(() => setSaveMessage(null), 3000);
    },
    onError: (err) => {
      setSaveMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Save failed.",
      });
    },
  });

  function updateThreshold(
    index: number,
    field: "warningValue" | "criticalValue",
    raw: string,
  ) {
    const value = parseFloat(raw);
    if (isNaN(value)) return;
    setThresholds((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)),
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-medium mb-1">KPI Thresholds</h3>
        <p className="text-xs text-muted-foreground">
          Warning and critical thresholds for each monitored KPI. Use "Suggest from data" to derive
          values from the metric's trailing history (Looker), then Save. Changes take effect on the next scan.
        </p>
      </div>

      {thresholds.some((t) => t.unsupported) && (
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
          <p>
            <span className="font-medium text-foreground">Gated KPIs</span> report{" "}
            <span className="font-mono">no data</span> and never alert — their backing Looker
            measure isn't validated yet (or doesn't exist). They won't fire until ungated in the
            agent's <span className="font-mono">runtimeConfig</span>.
          </p>
        </div>
      )}

      {suggestError && (
        <p className="text-xs text-destructive">{suggestError}</p>
      )}

      <div className="border border-border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[1fr_80px_120px_120px_100px] gap-3 px-4 py-2 bg-accent/30 border-b border-border text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
          <span>KPI</span>
          <span className="text-center">Cond.</span>
          <span className="text-center text-amber-600 dark:text-amber-400">Warning</span>
          <span className="text-center text-red-600 dark:text-red-400">Critical</span>
          <span className="text-center">Live</span>
        </div>

        {thresholds.length === 0 ? (
          <div className="px-4 py-6 text-sm text-muted-foreground text-center">
            No thresholds configured.
          </div>
        ) : (
          thresholds.map((threshold, i) => {
            const liveValue = currentKpis?.[threshold.key] ?? null;
            const status = threshold.unsupported
              ? null
              : computeStatus(liveValue, threshold);

            return (
              <div
                key={threshold.key}
                className={cn(
                  "grid grid-cols-[1fr_80px_120px_120px_100px] gap-3 px-4 py-3 items-center text-sm",
                  i !== thresholds.length - 1 && "border-b border-border",
                  threshold.unsupported && "bg-muted/20",
                )}
              >
                {/* Label + field + filters + data-driven suggestion */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{threshold.label}</p>
                    {threshold.unsupported && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-500/10 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                        <Ban className="h-2.5 w-2.5" />
                        Gated
                      </span>
                    )}
                  </div>
                  {threshold.lookerField ? (
                    <p className="text-[11px] text-muted-foreground font-mono truncate mt-0.5">
                      {threshold.lookerField}
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground italic mt-0.5">
                      no backing Looker measure
                    </p>
                  )}
                  {threshold.filters && Object.keys(threshold.filters).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {Object.entries(threshold.filters).map(([field, value]) => (
                        <span
                          key={field}
                          title={`${field} = ${value}`}
                          className="inline-flex max-w-full items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground"
                        >
                          <span className="truncate">
                            {field.split(".").pop()}: {value}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => suggestFromData(i)}
                    disabled={suggestingKey !== null || threshold.unsupported}
                    title={
                      threshold.unsupported
                        ? "Gated — validate the backing measure before suggesting thresholds"
                        : undefined
                    }
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline disabled:opacity-50 disabled:no-underline dark:text-blue-400"
                  >
                    {suggestingKey === threshold.key ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    Suggest from data
                  </button>
                  {suggestions[threshold.key] && (
                    <p className="mt-1 text-[10px] text-muted-foreground leading-snug">
                      {suggestions[threshold.key].suggestion.rationale}{" "}
                      <span className="text-muted-foreground/70">
                        (n={suggestions[threshold.key].stats.sampleSize},{" "}
                        {suggestions[threshold.key].window})
                      </span>
                    </p>
                  )}
                </div>

                {/* Condition pill */}
                <div className="flex justify-center">
                  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-mono bg-muted text-muted-foreground">
                    {threshold.condition}
                  </span>
                </div>

                {/* Warning input */}
                <div className="flex justify-center">
                  <Input
                    type="number"
                    value={threshold.warningValue}
                    onChange={(e) => updateThreshold(i, "warningValue", e.target.value)}
                    className={cn(
                      "w-24 h-8 text-sm text-center",
                      "border-amber-400/60 focus-visible:ring-amber-400/40 dark:border-amber-500/40",
                    )}
                    step="any"
                  />
                </div>

                {/* Critical input */}
                <div className="flex justify-center">
                  <Input
                    type="number"
                    value={threshold.criticalValue}
                    onChange={(e) => updateThreshold(i, "criticalValue", e.target.value)}
                    className={cn(
                      "w-24 h-8 text-sm text-center",
                      "border-red-400/60 focus-visible:ring-red-400/40 dark:border-red-500/40",
                    )}
                    step="any"
                  />
                </div>

                {/* Live status */}
                <div className="flex flex-col items-center gap-1">
                  {threshold.unsupported ? (
                    <span className="text-[10px] font-medium text-muted-foreground">no data</span>
                  ) : (
                    <>
                      <StatusPill status={status} />
                      {liveValue !== null && liveValue !== undefined && (
                        <span className="text-[10px] text-muted-foreground font-mono">
                          {liveValue.toFixed(1)}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button
          size="sm"
          onClick={() => updateAgent.mutate(thresholds)}
          disabled={updateAgent.isPending}
        >
          {updateAgent.isPending ? "Saving…" : "Save Thresholds"}
        </Button>
        {saveMessage && (
          <p
            className={cn(
              "text-xs",
              saveMessage.type === "success" ? "text-green-600 dark:text-green-400" : "text-destructive",
            )}
          >
            {saveMessage.text}
          </p>
        )}
      </div>
    </div>
  );
}
